import React from 'react';
import { string, array } from 'prop-types';
import { Grid } from "@material-ui/core";
import InfiniteScroll from 'react-infinite-scroll-component';
import { isEmpty, has, forEach, assign, isObject, isArray, uniqBy, eq, get, reject } from 'lodash';
import { TabsBlue, TabBlue, XLPrimaryButton } from '@worthy-npm/clarity';

import withMaterialUiTheme from "../../enhancers/withMaterialUiTheme";
import { isMobile, isTablet, goTo, FILTER_OPTIONS, SHAPES} from '../filters/filter_constants_and_helpers.jsx.erb';

import { post, get as getRequest } from '../../services/api';
import DealItem from './DealItem';
import RecentDealsFilters from "./RecentDealsFilters";
import Loading from "./Loading";
import { LOCAL_STORAGE_PREFIX, RDStorage } from './filtersData';

const ITEM_TYPES = ["rings","bracelets","earrings","necklaces","loose-diamonds","loose-gemstones", 'watches', 'all'];

export const changeHistoryState = (type) => history.pushState({}, '' , type ? `/about/recent-deals/${type}` : '/about/recent-deals'); // in order to don`t add / in the end for all case

export const createItemTypeChangeEvent = value => new CustomEvent('itemTypeChange', { detail: { itemType: value } });

const sortInCustomOrderItemTypes = (itemTypes) => { // { 0: Name, 1: key }
    const withPriorityProp = itemTypes.map(type => {
        // All, Rings, Loose diamonds, necklaces, bracelets, earrings, watches
        switch (type[0]) {
            case 'All': {
                return { ...type, 2: 1 }
            }
            case 'Rings': {
                return { ...type, 2: 2 }
            }
            case 'Loose Diamonds': {
                return { ...type, 2: 3 }
            }
            case 'Necklaces': {
                return { ...type, 2: 4 }
            }
            case 'Bracelets': {
                return { ...type, 2: 5 }
            }
            case 'Earrings': {
                return {...type, 2: 6}
            }
            case 'Watches': {
                return { ...type, 2: 7 }
            }
            default: {
                return { ...type, 2: 8 }
            }
        }
    });

    return withPriorityProp.sort((first, second) => first[2] - second[2]);
};

const getSingleFormOfItemType = (type) => {
    if (type === 'loose-diamonds') {
        return 'diamond';
    } else if (type === 'all') {
        return 'thing';
    } else if (type === 'watches') {
        return 'watch';
    } else {
        return type.substring(0, type.length - 1);
    }
};

export const itemTypeToSystemFormat = (type) => type.toLowerCase().replace(' ', '-')

const GA_EVENT_TYPES = {
    pagePresented: itemType => ({
        action: 'Page presented',
        label: itemType,
    }),
    tabClicked: tab => ({
        action: 'Tab clicked',
        label: tab,
    }),
    emptyStateBtnClicked: {
        action: 'Empty state CTA clicked',
        label: 'Empty state CTA clicked'
    },
    emptyStatePresented: appliedFilters => ({
        action: 'Empty state presented',
        label: appliedFilters,
    })
};

/*
*  Component for wrapping all together for recent deals
* */
class RecentDealsContainer extends React.Component {

    constructor(props) {
        super(props);

        this.state = {
            deals: [],
            isItemsLoaded: false,
            areFiltersOpen: false, // indicator for filters when they open on mobile,
            pageIndex: 1,
            appliedFilters: {},
            hasMoreItems: false,
            itemType: props.itemType,
            isFiltersInitialized: false, // this variable use in order to load all filters and make one request instread of doing each request on filter initialized.
            // cause each filter initialize itself and it cause updating of this component and view sometimes rerender a couple of times and it cause strange behaviour.
            isMobileFilterShown: false
        };

        this.initializedFiltersCount = 0; // will save count of filters which was for current itemType
        this.appliedTagNames = [], // names of applied filters uses for GA.

        this._getRecentDealsItems = this._getRecentDealsItems.bind(this);
        this._filtersChangeHandler = this._filtersChangeHandler.bind(this);
        this._onChangeFiltersVisibility = this._onChangeFiltersVisibility.bind(this);
        this._getItemTypeInNumber = this._getItemTypeInNumber.bind(this);
        this._handleItemTypeChange = this._handleItemTypeChange.bind(this);
        this._getUrlForSelling = this._getUrlForSelling.bind(this);
        this._allFiltersInitializedPerType = this._allFiltersInitializedPerType.bind(this);
        this.getItemProps = this.getItemProps.bind(this);
    }

    async getItemProps() {
        const url = window.location.href
        const itemId = url.includes('?') && url.split('?')[1].split('=')[1]
        const storageKey = `${LOCAL_STORAGE_PREFIX}item_${itemId}_filtered`

        if(!RDStorage.get(storageKey) && itemId) {
            getRequest(`/api/item_props?item_id=${itemId}`)
                .then(({ data }) => {
                    let validShapes = SHAPES.jewelry.map(e => {
                        return FILTER_OPTIONS.shapes.all.find(s => s.pretty_name === e).id
                    })
                    const filteredShapes = validShapes.filter(s => data.shape.includes(s))
                    data.shape = filteredShapes.length? filteredShapes : []
                    this.setState({itemFilters: data})
                    RDStorage.set(storageKey, true)
                })
        }
    }

    componentDidMount() {
        const { itemType } = this.props;

        this.getItemProps();
        this._pushGAEvent(GA_EVENT_TYPES.pagePresented(itemType));

        if (itemType === 'all' || !isMobile()) {
            this._getRecentDealsItems();
        }
    }

    componentDidUpdate(prevProps, prevState) {
        const isPropNotEqual = i => !eq(get(this.state, i), get(prevState, i));
        if (['itemType'].find(isPropNotEqual)) { // reset when item type has changed
            this.initializedFiltersCount = 0;
            this.appliedTagNames = [];
            this.setState({ deals: [] });
            this._getRecentDealsItems(1);
        }

        if (this.state.isItemsLoaded && this.state.deals.length === 0) {
            const normalizedNamesOfAppliedFilters = this.appliedTagNames.map(tag => tag.substring(0, tag.lastIndexOf(' '))).join(', '); // drop the filter name in the end of each filter
            this._pushGAEvent(GA_EVENT_TYPES.emptyStatePresented(normalizedNamesOfAppliedFilters));
        }
    }

    _filtersChangeHandler({ detail }) {
        const { appliedFilters } = this.state;

        if (!appliedFilters[detail.itemType]) {
            appliedFilters[detail.itemType] = { requiredFilters: detail.requiredFilters };
        }

        this.appliedTagNames = [ ...this.appliedTagNames, ...detail.tagNames ];

        this.setState({
            pageIndex: 1,
            appliedFilters: {
                ...appliedFilters,
                [detail.itemType]: assign(appliedFilters[detail.itemType], { [detail.filterName]: detail.data }),
            }
        });

        this.initializedFiltersCount = this.initializedFiltersCount + 1;
        this._getRecentDealsItems(1); // calling it from here instead of componentDidMount cause got issue with comparing big objects(with deep structure)
    }


    _allFiltersInitializedPerType() {
        const { itemType } = this.state;
        const COUNT_OF_GENERAL_FILTERS = 5;
        const COUNT_OF_DIAMOND_FILTERS = 4;
        const COUNT_OF_WATCH_FILTERS = 2;

        if (['necklaces', 'earrings', 'bracelets', 'rings'].includes(itemType) && this.initializedFiltersCount >= COUNT_OF_GENERAL_FILTERS) {
            return true;
        } else if (itemType === 'loose-diamonds' && this.initializedFiltersCount >= COUNT_OF_DIAMOND_FILTERS) {
            return true;
        } else if (itemType === 'watches' && this.initializedFiltersCount >= COUNT_OF_WATCH_FILTERS) {
            return true;
        } else if (itemType === 'all') {
            return true;
        } else {
            return false;
        }
    }

    _getRecentDealsItems(page) { // here i add param cause state didn`t updated
        const { pageIndex } = this.state;

        const pageToLoad = page || pageIndex;
        const convertDeals = (data) => data.data.map(item => item.get_deal)

        // prevent loading if filters is not initialized all
        if (!this._allFiltersInitializedPerType()) { // on mobile we don`t render it on the page, It apears in modal
            return
        }

        post(
            `/api/recentdeals/${this._getItemTypeInNumber()}?page=${pageToLoad}`,
            { filters: this._prepareAppliedFilters()}
            ).then(({ data }) => {
                const converted = convertDeals(data);
                this.setState({
                    deals: pageToLoad === 1 ? converted : [ ...this.state.deals, ...converted ],
                    hasMoreItems: !!data.next,
                    pageIndex: data.next,
                    isItemsLoaded: true
                });
            })
    }

    _onChangeFiltersVisibility(visibility) {
        this.setState({ areFiltersOpen: visibility });
    }

    _getItemTypeInNumber() {
        return this.props.itemTypes.find((item) => itemTypeToSystemFormat(item[0]) === this.state.itemType)[1]
    }

    _prepareAppliedFilters() {
        const { appliedFilters, itemType } = this.state;

        if (isEmpty(appliedFilters) || !ITEM_TYPES.includes(itemType) || !has(appliedFilters, itemType)) {
            return {}
        }
        let filterObject = {};

        appliedFilters[itemType].requiredFilters.forEach((filterClass) => {
            const filterName = filterClass.getFilterName();

            forEach(appliedFilters[itemType][filterName], (value, key) => {
                if(((isObject(value) || isArray(value)) && !isEmpty(value)) || (!(isObject(value) || isArray(value)) && !!value)) { // in order to push to server only filled filtes (without null)
                    filterObject[`${filterName}_${key}`] = value
                }
            })
        });

        return filterObject
    }

    _handleItemTypeChange(type) {
        this._pushGAEvent(GA_EVENT_TYPES.tabClicked(type));

        this.setState({
            itemType: type,
            isItemsLoaded: false, // reset this field in order to proper render No data screen
        });
    }

    _getUrlForSelling() {
        const { appUrl } = this.props;
        const { itemType } = this.state;

        if (['all', 'rings', 'loose-diamonds'].includes(itemType)) {
            return  appUrl;
        } else if (itemType === 'earrings') {
            return `${window.location.origin}/form`;
        } else {
            return `${appUrl}/submit/${getSingleFormOfItemType(itemType)}`
        }
    }

    _pushGAEvent({ action, label }) {
        dataLayer.push({
            'event':'GTM event To GA',
            'GA_event_category':'Recent deals',
            'GA_event_action': action,
            'GA_event_label': label
        })
    }

    render() {
        const { deals, areFiltersOpen, hasMoreItems, itemType, isItemsLoaded, itemFilters } = this.state;
        const { itemTypes } = this.props;

        const sortedItemTypes = sortInCustomOrderItemTypes(reject(itemTypes, i => i[0] === 'Loose Gemstones' || i[0] === 'Other'));

        const spacingAccordingToPlatform = isMobile() && !isTablet() ? 2 : 4;

        return (
            <Grid container
                  direction="column"
                  className="recent-deals-grid-container"
            >
                <div className="filters-first-line-wrapper">
                    <a className={`toggle-filters-button ${this.state.isMobileFilterShown ? 'opened' : ''}`} href="#" onClick={(e) => {
                        this.setState({ isMobileFilterShown: !this.state.isMobileFilterShown})
                        e.preventDefault()
                        return false;
                    }}>
                        Filters
                    </a>

                    <div className="item-tabs">
                        <TabsBlue
                            onChange={(prevValue, nextValue) => {
                                const currentType = itemTypeToSystemFormat(itemTypes.find((t) => t[1] === nextValue)[0]);

                                changeHistoryState(currentType === 'all' ? '' : currentType);
                                window.dispatchEvent(createItemTypeChangeEvent(currentType));
                                this._handleItemTypeChange(currentType);
                            }}
                            value={this._getItemTypeInNumber()}
                        >
                            {
                                sortedItemTypes.map(type => (
                                    <TabBlue style={{ marginBottom: '0px' }}
                                             key={type[1]}
                                             label={type[0] === 'Loose Diamonds' ? 'Diamonds' : type[0]}
                                             value={type[1]}
                                             dataAutomation={this._getItemTypeInNumber() === type[1] ? 'active' : ''}
                                             cla
                                    />
                                ))
                            }
                        </TabsBlue>
                    </div>
                </div>

                <RecentDealsFilters
                    itemType={itemType}
                    onChangeFilter={this._filtersChangeHandler}
                    itemFilters={itemFilters}
                    isMobileFilterShown={this.state.isMobileFilterShown}
                />

                {/* Row for items squares */}
                <InfiniteScroll
                    dataLength={deals.length}
                    next={this._getRecentDealsItems}
                    hasMore={hasMoreItems}
                    style={{ overflow: 'hidden', marginBottom: isMobile() ? '64px' : '44px', display: areFiltersOpen ? 'none' : 'block', minHeight: '19em' }}
                    loader={ <Loading /> }
                >
                    <Grid
                        container
                        spacing={spacingAccordingToPlatform}
                    >
                        {
                            deals.map((deal, i) => (<DealItem key={i} deal={deal} />))
                        }
                    </Grid>
                    {
                        isItemsLoaded ? deals.length === 0  && (<Grid container justify="center" style={{ marginTop: '64px' }}>
                            <Grid item style={{ textAlign: 'center' }} >
                                <p style={{ marginBottom: '27px', font: '300 16px Uncut Sans', lineHeight: '22px' }}>Be the first to sell that kind of { getSingleFormOfItemType(itemType) }!</p>
                                <div className='clarity-button-wrapper'>
                                    <XLPrimaryButton text="Start Selling"
                                                     click={() => {
                                                         this._pushGAEvent(GA_EVENT_TYPES.emptyStateBtnClicked);
                                                         goTo(this._getUrlForSelling())
                                                     }}
                                                     overrideCSS={{
                                                         backgroundColor: '#2D5DEC', borderRadius: '3px',
                                                         padding: '11px 22px 12px', fontFamily: 'Uncut Sans', fontSize: '16px'
                                                     }}
                                    />
                                </div>
                            </Grid>
                        </Grid>) : <Loading />
                    }

                </InfiniteScroll>
            </Grid>
        );
    }
}

RecentDealsContainer.propsTypes = {
    itemType: string,
    itemTypes: array, // contains array with 2 values like ['rings', 5]
    appUrl: string, // ref to worthy-app
};

export default withMaterialUiTheme(RecentDealsContainer);
